import { lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { useAuth } from "./auth/AuthContext";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { AppLayout } from "./components/AppLayout";
import { ADMIN_ROLES, AUDIT_ROLES, DOC_FLOW_ADMIN_ROLES, SYSTEM_ADMIN_ROLES } from "./nav";
import { LoginPage } from "./pages/LoginPage";
import { NotFoundPage } from "./pages/NotFoundPage";

// TD-0047: route-level code splitting (Suspense-межа — навколо Outlet в AppLayout).
const AdminHomePage = lazy(() => import("./pages/AdminHomePage").then((m) => ({ default: m.AdminHomePage })));
const AuditPage = lazy(() => import("./pages/AuditPage").then((m) => ({ default: m.AuditPage })));
const ChangePasswordPage = lazy(() => import("./pages/ChangePasswordPage").then((m) => ({ default: m.ChangePasswordPage })));
const CountersPage = lazy(() => import("./pages/CountersPage").then((m) => ({ default: m.CountersPage })));
const HelpArticlesPage = lazy(() => import("./pages/HelpArticlesPage").then((m) => ({ default: m.HelpArticlesPage })));
const DepartmentsPage = lazy(() => import("./pages/DepartmentsPage").then((m) => ({ default: m.DepartmentsPage })));
const DictionariesPage = lazy(() => import("./pages/DictionariesPage").then((m) => ({ default: m.DictionariesPage })));
const DictionaryRecordsPage = lazy(() => import("./pages/DictionaryRecordsPage").then((m) => ({ default: m.DictionaryRecordsPage })));
const DocTypeCardPage = lazy(() => import("./pages/DocTypeCardPage").then((m) => ({ default: m.DocTypeCardPage })));
const DocTypesPage = lazy(() => import("./pages/DocTypesPage").then((m) => ({ default: m.DocTypesPage })));
const HandlersPage = lazy(() => import("./pages/HandlersPage").then((m) => ({ default: m.HandlersPage })));
const RouteCardPage = lazy(() => import("./pages/RouteCardPage").then((m) => ({ default: m.RouteCardPage })));
const ExecutorPoolsPage = lazy(() => import("./pages/ExecutorPoolsPage").then((m) => ({ default: m.ExecutorPoolsPage })));
const ProcessTemplateCardPage = lazy(() => import("./pages/ProcessTemplateCardPage").then((m) => ({ default: m.ProcessTemplateCardPage })));
const ProcessTemplatesPage = lazy(() => import("./pages/ProcessTemplatesPage").then((m) => ({ default: m.ProcessTemplatesPage })));
const RobotCardPage = lazy(() => import("./pages/RobotCardPage").then((m) => ({ default: m.RobotCardPage })));
const RobotsPage = lazy(() => import("./pages/RobotsPage").then((m) => ({ default: m.RobotsPage })));
const RoutesListPage = lazy(() => import("./pages/RoutesListPage").then((m) => ({ default: m.RoutesListPage })));
const EmailTemplatesPage = lazy(() => import("./pages/EmailTemplatesPage").then((m) => ({ default: m.EmailTemplatesPage })));
const RolesPage = lazy(() => import("./pages/RolesPage").then((m) => ({ default: m.RolesPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const TaskTypeCardPage = lazy(() => import("./pages/TaskTypeCardPage").then((m) => ({ default: m.TaskTypeCardPage })));
const TaskTypesPage = lazy(() => import("./pages/TaskTypesPage").then((m) => ({ default: m.TaskTypesPage })));
const UsersPage = lazy(() => import("./pages/UsersPage").then((m) => ({ default: m.UsersPage })));
const ExternalSystemsPage = lazy(() =>
  import("./pages/ExternalSystemsPage").then((m) => ({ default: m.ExternalSystemsPage })),
);
const WorkCalendarPage = lazy(() => import("./pages/WorkCalendarPage").then((m) => ({ default: m.WorkCalendarPage })));

// ADR-044 D6: чистий AUDITOR має єдиний розділ — журнал аудиту.
function HomeRedirect() {
  const { user } = useAuth();
  const roles = user?.roles ?? [];
  const isAdmin = ADMIN_ROLES.some((role) => roles.includes(role));
  if (!isAdmin && roles.includes("AUDITOR")) {
    return <Navigate to="/admin/audit" replace />;
  }
  if (!isAdmin) {
    // Без адмін-ролей /admin дав би Forbidden — ведемо на доступний роут
    // (консистентно з ChangePasswordPage, VF-003 !459).
    return <Navigate to="/admin/groups" replace />;
  }
  return <Navigate to="/admin" replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          {/* Власник пулу може керувати ним без адміністративної ролі; API повертає лише його пули. */}
          <Route path="/admin/groups" element={<ExecutorPoolsPage />} />
          {/* Доступна будь-якому автентифікованому: сюди ж веде примус must_change_password. */}
          <Route path="/admin/change-password" element={<ChangePasswordPage />} />
          <Route path="/" element={<HomeRedirect />} />
          {/* ADR-044 D6: аудит відкритий SA та AUDITOR (право audit:read). */}
          <Route element={<ProtectedRoute requiredRoles={[...AUDIT_ROLES]} />}>
            <Route path="/admin/audit" element={<AuditPage />} />
          </Route>
          <Route element={<ProtectedRoute requiredRoles={[...ADMIN_ROLES]} />}>
            <Route path="/admin" element={<AdminHomePage />} />
            <Route path="/admin/process-templates" element={<ProcessTemplatesPage />} />
            <Route path="/admin/process-templates/:id" element={<ProcessTemplateCardPage />} />
            <Route path="/admin/robots" element={<RobotsPage />} />
            <Route path="/admin/robots/:id" element={<RobotCardPage />} />
            <Route path="/admin/email-templates" element={<EmailTemplatesPage />} />
            <Route path="/admin/settings" element={<SettingsPage />} />
            <Route path="/admin/help" element={<HelpArticlesPage />} />
            <Route path="/admin/handlers" element={<HandlersPage />} />

            <Route element={<ProtectedRoute requiredRoles={[...DOC_FLOW_ADMIN_ROLES]} />}>
              <Route path="/admin/doc-types" element={<DocTypesPage />} />
              <Route path="/admin/doc-types/:id" element={<DocTypeCardPage />} />
              <Route path="/admin/task-types" element={<TaskTypesPage />} />
              <Route path="/admin/task-types/:id" element={<TaskTypeCardPage />} />
              <Route path="/admin/routes" element={<RoutesListPage />} />
              <Route path="/admin/routes/:id" element={<RouteCardPage />} />
              <Route path="/admin/dictionaries" element={<DictionariesPage />} />
              <Route path="/admin/dictionaries/:id" element={<DictionaryRecordsPage />} />
              <Route path="/admin/work-calendar" element={<WorkCalendarPage />} />
              <Route path="/admin/counters" element={<CountersPage />} />
            </Route>

            <Route element={<ProtectedRoute requiredRoles={[...SYSTEM_ADMIN_ROLES]} />}>
              <Route path="/admin/users" element={<UsersPage />} />
              <Route path="/admin/departments" element={<DepartmentsPage />} />
              <Route path="/admin/external-systems" element={<ExternalSystemsPage />} />
              <Route path="/admin/roles" element={<RolesPage />} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}
